
import React from 'react';
import { Badge } from '@/components/ui/badge';
import MarkdownRenderer from '@/components/Posts/MarkdownRenderer';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface PostContentProps {
  post: BlogPost;
  readTime: string;
}

const PostContent: React.FC<PostContentProps> = ({ post, readTime }) => {
  return (
    <article className="max-w-4xl mx-auto">
      {/* Categories */}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.categories.map((category, index) => (
            <Badge key={index} variant="outline">{category}</Badge>
          ))}
        </div>
      )}

      {/* Title */}
      <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
        {post.title}
      </h1>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="relative h-[400px] mb-8 rounded-lg overflow-hidden">
          <img 
            src={post.featured_image} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Excerpt */}
      {post.excerpt && (
        <div className="text-xl font-medium text-muted-foreground mb-8 p-6 bg-muted/50 rounded-lg border-l-4 border-primary">
          {post.excerpt}
        </div>
      )}

      {/* Content */}
      <div className="prose prose-lg max-w-none mb-8">
        <MarkdownRenderer content={post.content} />
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold mb-4">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-sm">
                #{tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default PostContent;
